import { observer } from "mobx-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation } from "@/i18n";
import type { TMemberTeammate } from "@/types/teammates";
import { TeammatePermissionsList } from "./teammate-permissions";

interface TeammateConsentDialogProps {
  /** The teammate being turned on; `null` keeps the dialog closed. */
  teammate: TMemberTeammate | null;
  /** The consent write is out: both buttons wait. */
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The member's consent before a teammate acts in their name (NG-2622, design
 * F1): who acts, what it may do in one sentence, and the full permissions one
 * click away in place. Not `ConfirmDialog`, which has no slot for the
 * permission grid; the grid opens inside this dialog rather than stacking a
 * second one over it.
 */
export const TeammateConsentDialog = observer(function TeammateConsentDialog({
  teammate,
  saving,
  onCancel,
  onConfirm,
}: TeammateConsentDialogProps) {
  const { t } = useTranslation();
  const [showPermissions, setShowPermissions] = useState(false);
  // The dialog animates out after `teammate` goes back to null, so it keeps
  // drawing the last teammate it was opened for: without this the closing frames
  // read "Turn on ?" with no name or summary.
  const [shown, setShown] = useState(teammate);
  if (teammate !== null && teammate !== shown) {
    setShown(teammate);
    // Each opening starts with the permissions folded, however the last one ended
    // (a confirmed consent closes without anyone folding them).
    setShowPermissions(false);
  }
  const name = shown?.name ?? "";

  return (
    <Dialog
      open={teammate !== null}
      onOpenChange={(open) => {
        if (open || saving) return;
        setShowPermissions(false);
        onCancel();
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("profile.digital_teammates.consent.title", { name })}</DialogTitle>
          <DialogDescription>
            {t("profile.digital_teammates.consent.body", {
              name,
              summary: shown?.summary ?? "",
            })}
          </DialogDescription>
        </DialogHeader>
        {shown && showPermissions ? (
          <div className="max-h-72 overflow-x-hidden overflow-y-auto pr-3">
            <TeammatePermissionsList scopes={shown.scopes} />
          </div>
        ) : (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto self-start px-0"
            onClick={() => setShowPermissions(true)}
          >
            {t("digital_teammates.view_permissions")}
          </Button>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={() => {
              setShowPermissions(false);
              onCancel();
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button type="button" disabled={saving} onClick={onConfirm}>
            {saving ? <Spinner /> : null}
            {t("profile.digital_teammates.turn_on")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
